import { useState } from "react";
import { Filter, X } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { portfolioProjects } from "../mock";

const PortfolioPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const categories = ["All", ...new Set(portfolioProjects.map(p => p.category))];

  const filteredProjects = selectedCategory === "All"
    ? portfolioProjects
    : portfolioProjects.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '60px 60px'
          }}></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-block animate-fade-in">
              <Badge className="px-4 py-2 bg-gradient-to-r from-orange-500/20 to-yellow-400/20 border border-orange-400/40 text-orange-300 text-sm font-bold">
                🎨 Our Portfolio 🎨
              </Badge>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold animate-slide-in-left">
              My <span className="bg-gradient-to-r from-orange-500 via-yellow-400 to-orange-500 bg-clip-text text-transparent">Portfolio</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed animate-slide-in-right">
              Real projects from real clients - every single one personally completed by me.
            </p>
            <div className="golden-accent-line"></div>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-gradient-to-r from-black to-gray-900 border-y border-yellow-500/20 sticky top-20 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-2">
              <Filter className="text-yellow-400" size={20} />
              <span className="font-bold text-white">Filter by Category:</span>
            </div>

            {/* Desktop Filters */}
            <div className="hidden md:flex flex-wrap gap-3">
              {categories.map((category) => (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  variant={selectedCategory === category ? "default" : "outline"}
                  className={`transition-all duration-300 ${
                    selectedCategory === category
                      ? "bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-500 hover:to-orange-600 font-bold shadow-lg shadow-yellow-500/50"
                      : "border-yellow-500/30 text-gray-300 hover:bg-yellow-400/10 hover:border-yellow-400"
                  }`}
                >
                  {category}
                </Button>
              ))}
            </div>

            {/* Mobile Filter Button */}
            <Button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="md:hidden bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold"
              variant="outline"
            >
              <Filter size={18} className="mr-2" />
              {selectedCategory}
            </Button>
          </div>

          {/* Mobile Filter Dropdown */}
          {isFilterOpen && (
            <div className="md:hidden mt-4 p-4 bg-gray-50 rounded-lg space-y-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setIsFilterOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-3 rounded-lg transition-colors duration-300 ${
                    selectedCategory === category
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="text-gray-300">
              Showing <span className="font-bold text-yellow-400">{filteredProjects.length}</span> project{filteredProjects.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className="group animate-scale-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <Card className="dark-card border border-yellow-500/20 hover:border-yellow-400 overflow-hidden h-full">
                  <div className="image-zoom relative">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-yellow-400 text-black backdrop-blur-sm font-bold">
                        {project.year}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-6 space-y-4 bg-gradient-to-br from-gray-900 to-black">
                    <div className="space-y-2">
                      <Badge variant="outline" className="text-blue-400 border-blue-400">
                        {project.category}
                      </Badge>
                      <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors duration-300">
                        {project.title}
                      </h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {project.description}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-700">
                      {project.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full font-medium border border-gray-700"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <X className="mx-auto text-gray-400 mb-4" size={64} />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No projects found</h3>
              <p className="text-gray-600">Try selecting a different category</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-8 bg-gradient-to-br from-blue-50 to-orange-50 rounded-3xl p-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Like What You See?
            </h2>
            <p className="text-xl text-gray-600">
              Let's create something amazing together. Start your project today!
            </p>
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
              <a href="/contact">Start Your Project</a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortfolioPage;